// src/pages/register/RegisterEducation.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { auth } from '../../firebase'
import Turnstile from '../../components/Turnstile'
import Spinner from '../../components/Spinner'
import FilePickerField from '../../components/FilePickerField'
import StripeIdentityVerifyPanel from '../../components/StripeIdentityVerifyPanel'
import { PendingApprovalCard } from '../../components/PendingApprovalCard'
import { GraduationCap, ShieldCheck, CheckCircle2 } from 'lucide-react'
import {
  COUNTRY_OPTIONS,
  getCountryRule,
  getCurrencyForCountry,
  getDefaultRegionForCountry,
  isValidPhoneForCountry,
  isValidPostalCode,
  normalizeCountryCode,
  phonePlaceholderForCountry,
  normalizeBusinessTaxId,
  isValidBusinessTaxId,
  getBusinessTaxIdFieldLabel,
  getBusinessTaxIdShortLabel,
  getBusinessTaxIdPlaceholder,
  businessTaxIdValidationMessage,
  getProfessionalRegistrationFieldRule,
  toE164Phone,
} from '../../countryRules'

const EDUCATION_SPECIALTY_GROUPS: Record<string, string[]> = {
  'Early Childhood': [
    'Early childhood learning & development',
    'School readiness',
    'Literacy & numeracy foundations',
  ],
  'Primary School (K–6)': [
    'All primary subjects',
    'Maths, English, Science basics',
    'Reading & writing support',
    'NAPLAN preparation',
    'Homework & study coaching',
  ],
  'Junior High School (Years 7–9)': [
    'Maths (general, advanced)',
    'Science (general)',
    'English',
    'Study skills & assignment support',
  ],
  'Senior High School (Years 10–12)': [
    'Maths: Standard, Advanced, Extension',
    'Science: Chemistry, Biology, Physics',
    'English (Standard, Advanced, Extension)',
    'ATAR / HSC / VCE / QCE preparation',
    'Major exam coaching',
    'Legal studies',
    'Economics',
  ],
  'Tutoring & Exam Preparation': [
    'ATAR / HSC / VCE / QCE',
    'UMAT / UCAT preparation',
    'GAMSAT preparation',
    'LSAT / GMAT / GRE exam prep',
    'PTE / IELTS / TOEFL',
    'Selective school & scholarship tests',
    'Thesis research & academic writing',
    'Assignment support & proofreading',
  ],
  'Interview Preparation': [
    'University interview prep (medicine, dentistry, etc.)',
    'MMI & structured behavioural interview prep',
    'Scholarship and selective school interviews',
    'Job interview coaching (all industries)',
  ],
  'Vocational & Tertiary': [
    'TAFE subjects',
    'Professional certifications',
    'Apprenticeship support',
  ],
  'University & Research': [
    'University tutoring',
    'Research methods',
    'Academic writing',
    'Thesis support',
    'Systematic review & meta-analysis',
    'Data analysis (R/Python)',
    'Biostatistics',
    'Epidemiology',
    'Health economics',
  ],
  'Clinical & laboratory training': [
    'Pathology laboratory training',
    'Clinical skills training',
    'CPR & First Aid training',
    'Medical laboratory sciences',
    'Clinical research mentoring',
    'NATA / ISO laboratory standards',
    'Health & safety training',
    'Allied health education',
  ],
  'Health & Medical Sciences': [
    'Pharmacy tutoring',
    'Pharmacology',
    'Evidence-based medicine',
  ],
  'Language & ESL': [
    'ESL / ELL support',
    'Literacy coaching',
    'IELTS / PTE / TOEFL',
    'Interpreting & translation',
    'Vietnamese',
    'Mandarin',
    'Cantonese',
    'Korean',
    'Japanese',
    'French',
    'Spanish',
    'Arabic',
    'Hindi',
    'Other languages',
  ],
  'Music & Creative': [
    'Piano tutoring',
    'Classical piano',
    'Music theory',
    'Aural training',
    'Composition',
    'Performance coaching',
    'AMEB exam prep',
    'VCE Music prep',
    'Uni audition prep',
    'Choir conducting',
    'Vocal coaching',
    'Accompaniment coaching',
    'Primary music',
    'Year 7–9 music',
    'Year 10–12 music',
  ],
  'Art & Visual Arts': [
    'Visual Arts education',
    'Primary art teacher',
    'Creative learning',
    'Drawing & painting',
    'Figurative art',
    'Acrylic painting',
    'Arts integration (cross-curriculum)',
    'Creativity & self-expression',
  ],
  'Screenwriting & Creative Writing': [
    'Screenwriting Teacher',
    'Writing for Film & TV',
    'Creative Writing Educator',
    'Script Development',
    'Story Structure TV Writing',
    'Film Writing',
    'Character & Dialogue',
    'Pitching & Development',
    'Script Editing Adult Education',
    'Short Courses',
    'Industry-Based Teaching',
    'Screenwriting Fundamentals',
    'Creative Mentoring',
  ],
  'Learning Support': [
    'Learning difficulties support',
    'Special education',
    'Study skills coaching',
    'Career counselling',
    'Student wellbeing mentoring',
  ],
  'Education counselling': [
    'Education pathway counselling',
    'Course & institution selection',
    'Student visa support (education documents)',
    'Study planning & enrolment guidance',
    'International student support',
    'Australian education system guidance',
    'University & college admissions',
    'School & vocational pathways',
    'Offer letter & enrolment coordination',
    'Visa application coordination (with registered agents)',
    'Student compliance & progression support',
    'Education agent counselling (QEAC)',
    'English for Academic Purposes (EAP)',
    'Adult migrant education',
    'Workplace communication training',
    'Cross-cultural education & training',
    'Curriculum development',
    'Teacher training & professional development',
  ],
  'Counselling & Supervision': [
    'Clinical counselling supervision',
    'Counsellor professional supervision',
    'Trauma-informed practice',
    'Psychosocial intervention programs',
  ],
  'Health Sciences': [
    'Psychology tutoring',
    'Cognitive psychology (ageing)',
    'Neuroscience',
    'Brain health & cognition',
    'Quantitative research methods',
    'Nutritional epidemiology',
    'Health sciences research',
  ],
  'Special Skills & Enrichment': [
    'Public speaking & debating',
    'Creative writing',
    'Critical thinking training',
    'Time-management & productivity coaching',
    'Mathematics enrichment',
    'Gifted education',
    'Mathematics professional development',
    'Mathematical logic & reasoning',
    'Decision theory',
    'Game theory',
    'Logic & reasoning',
    'Philosophy (critical reasoning)',
  ],
  'Technology & Coding': [
    'Coding & computer skills',
    'Full-stack web development',
    'Backend development & APIs',
    'Frontend web development (HTML/CSS/JavaScript)',
    'Database design & SQL',
    'Cloud & server-side development',
    'Software engineering fundamentals',
    'Object-oriented programming (OOP)',
    'DevOps & CI/CD basics',
    'Version control (Git/GitHub)',
    'Adult & vocational IT education',
    'Artificial intelligence',
  ],
  'Accounting & Bookkeeping Training': [
    'Accounting education & training',
    'Bookkeeping training',
    'Tax training (practical)',
    'Cloud accounting training',
    'Xero training',
    'QuickBooks training',
    'MYOB training',
    'Advanced bookkeeping',
    'Small business accounting training',
    'Accounting systems implementation training',
    'Professional upskilling (accounting)',
    'Career mentoring (accounting)',
    'Entry-level accounting training',
    'Accounting practice systems training',
    'Online accounting education',
  ],
}

const ALL_SPECIALTIES: string[] = Object.values(EDUCATION_SPECIALTY_GROUPS).flat()

const EDUCATION_API_BASE =
  import.meta.env.VITE_EDUCATION_API_BASE ||
  import.meta.env.VITE_API_EDUCATION_BASE ||
  import.meta.env.VITE_API_BASE

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined

const normalizeSpec = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .replace(/s$/, '')

const normalizeAbn = (value: string = '', country: unknown = 'AU') => normalizeBusinessTaxId(value, country)
const isValidAbn = (value: string = '', country: unknown = 'AU') => isValidBusinessTaxId(value, country)
const slug = (t: string) => `sp-${t.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
const hasStripeIdentityVerified = (record: any) => {
  if (!record || typeof record !== 'object') return false
  if (record.stripeIdentityVerifiedAt) return true
  const status = String(record.stripeIdentityStatus || '').trim().toLowerCase()
  return status === 'verified' || status === 'passed' || status === 'success'
}

async function getTokenOrThrow() {
  const u = auth.currentUser
  if (!u) throw new Error('Please sign in first.')
  return await u.getIdToken()
}

async function presign(keyHint: string, contentType: string) {
  if (!EDUCATION_API_BASE) throw new Error('Education API not configured')
  const token = await getTokenOrThrow()
  const r = await fetch(EDUCATION_API_BASE + '/uploads/presign', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
    body: JSON.stringify({ keyHint, contentType }),
  })
  const j = await r.json()
  if (!r.ok) throw new Error(j.error || 'Presign failed')
  return j as { url: string; fields: Record<string, string>; key?: string }
}

async function uploadToS3(p: { url: string; fields: Record<string, string> }, file: File) {
  const fd = new FormData()
  Object.entries(p.fields).forEach(([k, v]) => fd.append(k, v))
  fd.append('file', file)
  const up = await fetch(p.url, { method: 'POST', body: fd })
  if (!up.ok) {
    let detail = ''
    try {
      detail = await up.text()
    } catch {
      detail = up.statusText
    }
    throw new Error(detail || 'Upload failed')
  }
}

const fileKeyFromPresign = (psg: any) => psg?.key || psg?.fields?.key || null

const Section: React.FC<{ title?: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="section">
    {title && <h1 className="section-title">{title}</h1>}
    {children}
  </section>
)

type RegisterEducationProps = {
  user?: any
  variant?: 'full' | 'inline'
  inlineNotice?: React.ReactNode
  onSubmitSuccess?: () => void
  initialProfile?: any
  adminMode?: boolean
  adminPresign?: (keyHint: string, contentType: string) => Promise<{ url: string; fields: Record<string, string>; key?: string }>
  onAdminSubmit?: (payload: any) => Promise<void>
}

type Status = 'UNKNOWN' | 'NONE' | 'PENDING' | 'APPROVED'
type SaveState = 'idle' | 'saving' | 'saved' | 'error'

export default function RegisterEducation({
  user,
  variant = 'full',
  inlineNotice,
  onSubmitSuccess,
  initialProfile,
  adminMode = false,
  adminPresign,
  onAdminSubmit,
}: RegisterEducationProps) {
  const isInline = variant === 'inline'
  const PROFILE_CHAR_LIMIT = 1000
  const displayNameParts = (user?.displayName || '').trim().split(/\s+/)
  const initialFirst = displayNameParts.length > 1 ? displayNameParts.slice(0, -1).join(' ') : displayNameParts[0] || ''
  const initialLast = displayNameParts.length > 1 ? displayNameParts.slice(-1).join(' ') : ''
  const loginEmailFromUser = (u?: any) => (u?.email || auth.currentUser?.email || '').trim()
  const loginEmail = loginEmailFromUser(user)
  const [regStatus, setRegStatus] = useState<Status>('UNKNOWN')
  const [loadingStatus, setLoadingStatus] = useState(() => (!!user && !adminMode))
  const [err, setErr] = useState('')
  const [info, setInfo] = useState('')
  const [ok, setOk] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [tToken, setTToken] = useState('')
  const [showTS, setShowTS] = useState(false)
  const [waitingTS, setWaitingTS] = useState(false)
  const [step, setStep] = useState(0)
  const [showErrors, setShowErrors] = useState(false)
  const [validatedStep, setValidatedStep] = useState<number | null>(null)
  const [docsAttempted, setDocsAttempted] = useState(false)
  const [pricingErrors, setPricingErrors] = useState<Record<string, string>>({})
  const prefillRef = useRef(false)
  const profileVersionRef = useRef<string | null>(null)
  const saveTimerRef = useRef<number | null>(null)
  const [saveStatus, setSaveStatus] = useState<SaveState>('idle')
  const [saveVisible, setSaveVisible] = useState(false)
  const [syncPublicContact, setSyncPublicContact] = useState(true)
  const [showOptionalIdentity, setShowOptionalIdentity] = useState(false)

  const [s, setS] = useState<any>({
    firstName: initialFirst,
    lastName: initialLast,
    email: loginEmail,
    countryCode: 'AU',
    phone: '',
    street: '',
    city: '',
    state: 'NSW',
    postcode: '',
    abn: '',
    teacherRegistrationNumber: '',
    languagesOther: '',
    specialties: [],
    customSpecialties: [],
    profile: '',
    cvFile: null,
    credentialFile: null,
    photoFile: null,
    idFile1: null,
    practiceName: '',
    publicEmail: loginEmail,
    publicPhone: '',
    pricingMin: '',
    pricingMax: '',
    pricingNotes: '',
  })
  const [docKeys, setDocKeys] = useState<{
    photoKey: string | null
    teachingCredentialKey: string | null
    id1Key: string | null
    cvKey: string | null
  }>({
    photoKey: null,
    teachingCredentialKey: null,
    id1Key: null,
    cvKey: null,
  })
  const [stripeIdentityVerified, setStripeIdentityVerified] = useState(false)

  const countryCode = normalizeCountryCode(s.countryCode)
  const countryRule = getCountryRule(countryCode)
  const regionOptions = countryRule.regionOptions || []
  const phonePlaceholder = phonePlaceholderForCountry(countryCode)
  const taxIdLabel = getBusinessTaxIdFieldLabel(countryCode)
  const taxIdShortLabel = getBusinessTaxIdShortLabel(countryCode)
  const taxIdPlaceholder = getBusinessTaxIdPlaceholder(countryCode)
  const taxIdError = businessTaxIdValidationMessage(countryCode)
  const educationRegistrationRule = getProfessionalRegistrationFieldRule('education', countryCode)
  const feeCurrency = getCurrencyForCountry(countryCode)

  const steps = [
    'Identity & compliance',
    'Specialties',
    'Profile & pricing',
    'Documents & verification',
  ] as const

  const [specOpen, setSpecOpen] = useState(false)
  const [specQuery, setSpecQuery] = useState('')
  const [customSpec, setCustomSpec] = useState('')
  const [customSpecErr, setCustomSpecErr] = useState('')

  const fileNameFromKey = (k?: string | null) => {
    if (!k) return ''
    const parts = k.split('/')
    return parts[parts.length - 1] || k
  }

  const filteredSpecialties = useMemo(() => {
    const q = specQuery.toLowerCase().trim()
    return Object.entries(EDUCATION_SPECIALTY_GROUPS).map(([group, items]) => {
      const filtered = q ? items.filter((sp) => sp.toLowerCase().includes(q)) : items
      return { group, items: filtered }
    })
  }, [specQuery])

  const customSuggestions = useMemo(() => {
    const q = customSpec.toLowerCase().trim()
    if (q.length < 2) return []
    const source = [...ALL_SPECIALTIES, ...s.customSpecialties, ...s.specialties]
    return source
      .filter((sp) => {
        const n = normalizeSpec(sp)
        const nq = normalizeSpec(q)
        return sp.toLowerCase().includes(q) || n.includes(nq) || nq.includes(n)
      })
      .slice(0, 4)
  }, [customSpec, s.customSpecialties, s.specialties])

  const addCustomSpecialty = () => {
    const val = customSpec.trim()
    if (!val) return
    const norm = normalizeSpec(val)
    const dupCore = s.specialties.some((sp: string) => normalizeSpec(sp) === norm)
    const dupCustom = s.customSpecialties.some((sp: string) => normalizeSpec(sp) === norm)
    if (dupCore || dupCustom) {
      setCustomSpecErr('Already added (or very similar)')
      return
    }
    const similar = customSuggestions.some((sp) => {
      const n = normalizeSpec(sp)
      return n === norm || n.includes(norm) || norm.includes(n)
    })
    if (similar) {
      setCustomSpecErr('Very similar exists—please pick the existing one')
      return
    }
    setS((prev: any) => ({ ...prev, customSpecialties: [...prev.customSpecialties, val] }))
    setCustomSpec('')
    setCustomSpecErr('')
  }

  const splitFullName = (full: string) => {
    const parts = (full || '').trim().split(/\s+/).filter(Boolean)
    if (!parts.length) return { first: '', last: '' }
    if (parts.length === 1) return { first: parts[0], last: '' }
    return { first: parts.slice(0, -1).join(' '), last: parts.slice(-1).join(' ') }
  }

  const setFromProfile = useCallback(
    (data: any, opts?: { force?: boolean }) => {
      if (!data) return
      const force = opts?.force || false
      if (!force && prefillRef.current) return
      prefillRef.current = true
      // In admin mode, always prefer the live profile and ignore drafts so edits reflect approved data.
      const source = adminMode ? data : data.draftProfile || data
      if (!source) return
      const admin = source.adminContact || data.adminContact || {}
      const adminAddr = admin.address || {}
      const addr = source.address || data.address || {}
      const pricing = source.pricing || data.pricing || {}
      const canonicalEmail = loginEmail || source.email || data.email || ''
      const publicEmail = source.publicEmail || admin.email || canonicalEmail
      const publicPhone = source.publicPhone || admin.phone || ''
      const publicCity = source.publicCity || adminAddr.city || adminAddr.suburb || ''
      const sourceCountryCode = normalizeCountryCode(
        source.countryCode ||
        addr.countryCode ||
        adminAddr.countryCode ||
        source.country ||
        addr.country ||
        adminAddr.country ||
        'AU',
      )
      const publicState = source.publicState || adminAddr.state || getDefaultRegionForCountry(sourceCountryCode)
      const publicPostcode = source.publicPostcode || adminAddr.postcode || adminAddr.postalCode || ''
      const clean = (val?: string) => (val || '').trim().toLowerCase()
      const sameEmail = publicEmail && clean(publicEmail) === clean(canonicalEmail)
      const samePhone =
        publicPhone &&
        source.phone &&
        toE164Phone(publicPhone, sourceCountryCode) === toE164Phone(source.phone, sourceCountryCode)
      const sameCity = publicCity && clean(publicCity) === clean(addr.city)
      const sameState = publicState && clean(publicState) === clean(addr.state)
      const samePostcode = publicPostcode && clean(publicPostcode) === clean(addr.postcode)
      const syncPublic = sameEmail && samePhone && sameCity && sameState && samePostcode
      const languages = Array.isArray(source.languages)
        ? source.languages.filter((l: string) => l && l.toLowerCase() !== 'english')
        : []
      const custom = Array.isArray(source.customSpecialties)
        ? source.customSpecialties.map((c: string) => c.replace(/^other:\s*/i, '').replace(/^Other:\s*/i, '')).filter(Boolean)
        : []
      const fullName = source.name || ''
      const parsed = splitFullName(fullName)
      setS((prev: any) => ({
        ...prev,
        firstName: source.firstName || parsed.first || prev.firstName,
        lastName: source.lastName || parsed.last || prev.lastName,
        email: canonicalEmail || prev.email,
        countryCode: sourceCountryCode,
        phone: source.phone || prev.phone,
        street: addr.street || prev.street,
        city: addr.city || addr.suburb || addr.town || prev.city,
        state: addr.state || prev.state,
        postcode: addr.postcode || addr.postalCode || prev.postcode,
        abn: source.abn || prev.abn,
        teacherRegistrationNumber: source.teacherRegistrationNumber || source.teacherLicense || prev.teacherRegistrationNumber,
        languagesOther: languages.join(', '),
        specialties: Array.isArray(source.specialties) && source.specialties.length ? source.specialties : prev.specialties,
        customSpecialties: custom.length ? custom : prev.customSpecialties,
        profile: source.profile || prev.profile,
        practiceName: source.practiceName || prev.practiceName,
        publicEmail,
        publicPhone: publicPhone || prev.publicPhone,
        pricingMin: pricing.minAud != null ? String(pricing.minAud) : prev.pricingMin,
        pricingMax: pricing.maxAud != null ? String(pricing.maxAud) : prev.pricingMax,
        pricingNotes: pricing.notes || prev.pricingNotes,
      }))
      setDocKeys((prev) => ({
        photoKey: source.documents?.photoKey || prev.photoKey,
        teachingCredentialKey: source.documents?.teachingCredentialKey || source.documents?.teacherLicenseKey || prev.teachingCredentialKey,
        id1Key: source.documents?.id1Key || prev.id1Key,
        cvKey: source.documents?.cvKey || prev.cvKey,
      }))
      setStripeIdentityVerified(
        (prev) =>
          prev ||
          hasStripeIdentityVerified(data) ||
          hasStripeIdentityVerified(data?.profile) ||
          hasStripeIdentityVerified(data?.draftProfile) ||
          hasStripeIdentityVerified(source)
      )
      setSyncPublicContact(syncPublic)
    },
    [loginEmail, adminMode]
  )

  const loadStatus = useCallback(async () => {
    if (adminMode) {
      setRegStatus('NONE')
      setLoadingStatus(false)
      return
    }
    if (!user) {
      setRegStatus('NONE')
      setLoadingStatus(false)
      return
    }
    try {
      setLoadingStatus(true)
      if (!EDUCATION_API_BASE) throw new Error('Education API not configured')
      const token = await getTokenOrThrow()
      const res = await fetch(`${EDUCATION_API_BASE}/work/education/me`, {
        method: 'GET',
        headers: { Authorization: 'Bearer ' + token },
      })
      const json = await res.json()
      if (!res.ok) throw new Error(json?.error || 'Failed to load')
      const status = (json.status as Status) || 'NONE'
      setRegStatus(status)
      if (json.profile) setFromProfile(json, { force: true })
      setErr('')
    } catch (e: any) {
      console.error(e)
      setErr(e?.message || 'Failed to load status')
      setRegStatus('NONE')
    } finally {
      setLoadingStatus(false)
    }
  }, [setFromProfile, user])

  useEffect(() => {
    loadStatus()
  }, [loadStatus])

  useEffect(() => {
    if (!initialProfile) return
    const version =
      initialProfile.updatedAt ||
      initialProfile.status ||
      JSON.stringify({
        name: initialProfile.name,
        profile: initialProfile.profile,
        specialties: initialProfile.specialties,
      })
    if (profileVersionRef.current === version) return
    profileVersionRef.current = version
    prefillRef.current = false
    setFromProfile(initialProfile, { force: true })
    if (!adminMode) {
      const nextStatus = String(initialProfile.approvalStatus || initialProfile.status || '').toUpperCase() as Status
      if (nextStatus === 'APPROVED' || nextStatus === 'PENDING') {
        setRegStatus(nextStatus)
      }
    }
  }, [initialProfile, setFromProfile, adminMode])

  const handleDocSelect = useCallback(
    async (field: keyof typeof docKeys, file: File | null, hint: string) => {
      try {
        const setFile = (prev: any, f: File | null) => {
          if (field === 'cvKey') return { ...prev, cvFile: f }
          if (field === 'teachingCredentialKey') return { ...prev, credentialFile: f }
          if (field === 'id1Key') return { ...prev, idFile1: f }
          if (field === 'photoKey') return { ...prev, photoFile: f }
          return prev
        }
        if (!file) {
          setDocKeys((prev) => ({ ...prev, [field]: null }))
          setS((prev: any) => setFile(prev, null))
          return
        }
        setS((prev: any) => setFile(prev, file))
        const presignFn = adminMode && adminPresign ? adminPresign : presign
        const psg = await presignFn(hint, file.type || 'application/octet-stream')
        await uploadToS3(psg, file)
        const key = fileKeyFromPresign(psg)
        setDocKeys((prev) => ({ ...prev, [field]: key }))
        setErr('')
      } catch (e: any) {
        setErr(e?.message || 'File upload failed')
      }
    },
    []
  )

  const buildDraftPayload = useCallback(() => {
    const contactEmail = (loginEmail || s.email || '').trim() || null
    const publicEmail = syncPublicContact ? contactEmail : (s.publicEmail || '').trim() || null
    const publicPhone = syncPublicContact ? s.phone : s.publicPhone
    const publicCity = s.city
    const publicState = s.state
    const publicPostcode = s.postcode
    const pricingMin = Number(s.pricingMin)
    const pricingMax = Number(s.pricingMax)
    const pricing =
      Number.isFinite(pricingMin) || Number.isFinite(pricingMax)
        ? {
            minAud: Number.isFinite(pricingMin) ? Number(pricingMin.toFixed(2)) : null,
            maxAud: Number.isFinite(pricingMax) ? Number(pricingMax.toFixed(2)) : null,
            medicareRebate: false,
            notes: (s.pricingNotes || '').trim() || null,
          }
        : null
    return {
      firstName: (s.firstName || '').trim() || null,
      lastName: (s.lastName || '').trim() || null,
      name: `${s.firstName || ''} ${s.lastName || ''}`.replace(/\s+/g, ' ').trim() || null,
      email: contactEmail,
      phone: s.phone || null,
      abn: s.abn ? normalizeAbn(s.abn, countryCode) : null,
      address: {
        street: s.street || '',
        city: s.city || '',
        state: s.state || getDefaultRegionForCountry(countryCode),
        postcode: s.postcode || '',
        country: countryRule.label,
        countryCode,
      },
      teacherRegistrationNumber: s.teacherRegistrationNumber || null,
      languages: ['English', ...s.languagesOther.split(',').map((x: string) => x.trim()).filter(Boolean)],
      specialties: s.specialties || [],
      customSpecialties: (s.customSpecialties || []).map((v: string) => `Other: ${v}`),
      practiceName: s.practiceName || null,
      publicPractice: s.practiceName || null,
      adminContact: {
        email: publicEmail,
        phone: publicPhone ? toE164Phone(publicPhone, countryCode) : null,
        address: {
          street: s.street || '',
          city: publicCity || '',
          state: publicState || '',
          postcode: publicPostcode || '',
          country: countryRule.label,
          countryCode,
        },
        practiceName: s.practiceName || null,
      },
      publicEmail,
      publicPhone,
      publicStreet: s.street,
      publicCity,
      publicState,
      publicPostcode,
      pricing,
      currency: feeCurrency,
      documents: docKeys,
      modes: ['TELEHEALTH'],
      availability: [],
      feeRange: null,
      subspecialties: [],
      profile: s.profile?.trim() || null,
    }
  }, [loginEmail, s, syncPublicContact, docKeys, countryCode, countryRule, feeCurrency])

  const saveDraft = useCallback(async () => {
    if (adminMode) return
    try {
      setSaveVisible(true)
      setSaveStatus('saving')
      if (!EDUCATION_API_BASE) throw new Error('Education API not configured')
      const token = await getTokenOrThrow()
      const payload = buildDraftPayload()
      await fetch(`${EDUCATION_API_BASE}/work/education/draft`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
        body: JSON.stringify(payload),
      })
      setSaveStatus('saved')
    } catch (e) {
      setSaveStatus('error')
    }
  }, [buildDraftPayload])

  useEffect(() => {
    if (adminMode || !user || regStatus === 'APPROVED') return
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current)
    }
    setSaveVisible(true)
    setSaveStatus('saving')
    saveTimerRef.current = window.setTimeout(() => {
      saveDraft()
      saveTimerRef.current = null
    }, 800)
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current)
        saveTimerRef.current = null
      }
    }
  }, [s, docKeys, syncPublicContact, user, regStatus, saveDraft])

  const validatePricing = useCallback(() => {
    const errs: Record<string, string> = {}
    const min = Number(s.pricingMin)
    const max = Number(s.pricingMax)
    if (!Number.isFinite(min)) {
      errs.min = 'Enter a minimum fee'
    } else if (min < 0 || min > 10000) {
      errs.min = 'Must be between $0 and $10,000'
    }
    if (!Number.isFinite(max)) {
      errs.max = 'Enter a maximum fee'
    } else if (max < 0 || max > 10000) {
      errs.max = 'Must be between $0 and $10,000'
    }
    if (Number.isFinite(min) && Number.isFinite(max) && max < min) {
      errs.max = 'Max must be greater than or equal to min'
    }
    const notes = (s.pricingNotes || '').trim()
    if (notes.length > 200) {
      errs.notes = 'Notes must be 200 characters or less'
    }
    setPricingErrors(errs)
    if (Object.keys(errs).length) return null
    return {
      minAud: Number(min.toFixed(2)),
      maxAud: Number(max.toFixed(2)),
      medicareRebate: false,
      notes,
    }
  }, [s.pricingMin, s.pricingMax, s.pricingNotes])
  const hasIdentityProof = !!docKeys.id1Key || stripeIdentityVerified

  const validateStep = useCallback((idx: number): string[] => {
    const issues: string[] = []
    if (idx === 0) {
      const contactEmail = (loginEmail || s.email || '').trim()
      if (!s.firstName || !s.lastName) issues.push('Add your first and last name.')
      if (!contactEmail) issues.push('Email is required.')
      if (!s.phone || !isValidPhoneForCountry(s.phone, countryCode)) {
        issues.push('Add a valid phone number for the selected country.')
      }
      if (s.abn && !isValidAbn(s.abn, countryCode)) issues.push(taxIdError)
      if (!s.street.trim()) issues.push('Add your street address.')
      if (!s.city.trim()) issues.push('Add your suburb/city.')
      if (countryRule.requireRegion && !s.state) issues.push(`Select your ${countryRule.regionLabel.toLowerCase()}.`)
      if (countryRule.requirePostal && !s.postcode) issues.push(`Add your ${countryRule.postalLabel.toLowerCase()}.`)
      if (s.postcode && !isValidPostalCode(countryCode, s.postcode)) {
        issues.push(`Enter a valid ${countryRule.postalLabel.toLowerCase()}.`)
      }
    } else if (idx === 1) {
      if (!s.specialties.length) issues.push('Select at least one specialty.')
    } else if (idx === 2) {
      if (!(s.profile || '').trim()) issues.push('Add your public profile.')
      const pricingOk = validatePricing()
      if (!pricingOk) issues.push('Fix pricing errors.')
    } else if (idx === 3) {
      if (!docKeys.cvKey) issues.push('Upload your CV / Resume, Portfolio, or Supporting Documents.')
      if (!hasIdentityProof) issues.push('Upload a government-issued photo ID or complete Stripe ID verification.')
      if (!docKeys.photoKey) issues.push('Upload a profile photo.')
      if (!docKeys.cvKey || !hasIdentityProof || !docKeys.photoKey) {
        return issues.length ? [issues[0]] : []
      }
    }
    return issues
  }, [s, loginEmail, docKeys, validatePricing, countryCode, countryRule, hasIdentityProof])

  const validateAllSteps = useCallback(() => {
    for (let i = 0; i < steps.length; i++) {
      const issues = validateStep(i)
      if (issues.length) return { ok: false, index: i, message: issues[0] }
    }
    return { ok: true, index: steps.length - 1, message: '' }
  }, [steps.length, validateStep])

  useEffect(() => {
    setErr('')
    setInfo('')
    setShowErrors(false)
    setValidatedStep(null)
    setDocsAttempted(false)
    setShowTS(false)
    setWaitingTS(false)
    setSubmitting(false)
    setTToken('')
  }, [step])

  useEffect(() => {
    if (step === 3) {
      setErr('')
      setShowErrors(false)
      setValidatedStep(null)
    }
  }, [step])

  useEffect(() => {
    if (waitingTS && tToken) {
      setWaitingTS(false)
      submitApplication()
    }
  }, [waitingTS, tToken])

  const buildSubmitPayload = useCallback(() => {
    const contactEmail = (loginEmail || s.email || '').trim()
    const publicEmail = syncPublicContact ? contactEmail : (s.publicEmail || '').trim() || null
    const publicPhone = syncPublicContact ? s.phone : s.publicPhone
    const publicCity = s.city
    const publicState = s.state
    const publicPostcode = s.postcode
    const pricing = validatePricing()
    if (!pricing) return null
    return {
      title: 'Education & Tutoring',
      firstName: (s.firstName || '').trim(),
      lastName: (s.lastName || '').trim(),
      name: `${s.firstName} ${s.lastName}`.replace(/\s+/g, ' ').trim(),
      email: contactEmail,
      phone: toE164Phone(s.phone, countryCode),
      abn: s.abn ? normalizeAbn(s.abn, countryCode) : null,
      teacherRegistrationNumber: s.teacherRegistrationNumber,
      address: {
        street: s.street.trim(),
        city: s.city.trim(),
        state: s.state,
        postcode: s.postcode.trim(),
        country: countryRule.label,
        countryCode,
      },
      languages: ['English', ...s.languagesOther.split(',').map((x: string) => x.trim()).filter(Boolean)],
      specialties: s.specialties,
      customSpecialties: s.customSpecialties.map((c: string) => `Other: ${c}`),
      practiceName: s.practiceName.trim() || null,
      publicPractice: s.practiceName.trim() || null,
      adminContact: {
        email: publicEmail,
        phone: publicPhone ? toE164Phone(publicPhone, countryCode) : null,
        address: {
          street: s.street.trim(),
          city: publicCity.trim(),
          state: publicState,
          postcode: publicPostcode.trim(),
          country: countryRule.label,
          countryCode,
        },
        practiceName: s.practiceName.trim() || null,
      },
      publicEmail: publicEmail || contactEmail,
      publicPhone: publicPhone || s.phone,
      publicStreet: s.street,
      publicCity: publicCity || s.city,
      publicState: publicState || s.state,
      publicPostcode: publicPostcode || s.postcode,
      pricing,
      currency: feeCurrency,
      documents: docKeys,
      modes: ['TELEHEALTH'],
      availability: [],
      feeRange: null,
      subspecialties: [],
      profile: (s.profile || '').trim(),
      turnstileToken: tToken,
    }
  }, [s, docKeys, tToken, loginEmail, syncPublicContact, validatePricing, countryCode, countryRule, feeCurrency])

  const submitApplication = useCallback(async () => {
    const all = validateAllSteps()
    if (!all.ok) {
      setStep(all.index)
      setErr(all.message)
      setShowErrors(true)
      setValidatedStep(all.index)
      if (all.index === 3) setDocsAttempted(true)
      return
    }
    const payloadBase = buildSubmitPayload()
    if (!payloadBase) {
      setErr('Please fix the highlighted fields')
      return
    }
    const payload: any = payloadBase
    try {
      setSubmitting(true)
      if (adminMode) {
        if (initialProfile?.userId) {
          payload.userId = initialProfile.userId
        }
        if (onAdminSubmit) {
          await onAdminSubmit(payload)
        } else {
          if (!EDUCATION_API_BASE) throw new Error('Education API not configured')
          const token = await getTokenOrThrow()
          const res = await fetch(`${EDUCATION_API_BASE}/admin/providers/${payload.userId || ''}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
            body: JSON.stringify(payload),
          })
          const json = await res.json()
          if (!res.ok) throw new Error(json?.error || 'Failed to submit')
        }
        setOk(true)
        setRegStatus('NONE')
        setErr('')
        setInfo('Changes saved.')
        if (onSubmitSuccess) onSubmitSuccess()
      } else {
        await auth.currentUser?.reload()
        await auth.currentUser?.getIdToken(true)
        if (!payload.turnstileToken) {
          throw new Error('Please verify with Turnstile before submitting.')
        }
        if (!EDUCATION_API_BASE) throw new Error('Education API not configured')
        const token = await getTokenOrThrow()
        const res = await fetch(`${EDUCATION_API_BASE}/work/education`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
          body: JSON.stringify(payload),
        })
        const json = await res.json()
        if (!res.ok) throw new Error(json?.error || 'Failed to submit')
        setOk(true)
        setRegStatus('PENDING')
        setErr('')
        setInfo('Application submitted — we’ll email you once it’s reviewed.')
        if (onSubmitSuccess) onSubmitSuccess()
      }
    } catch (e: any) {
      console.error(e)
      setErr(e?.message || 'Failed to submit')
    } finally {
      setSubmitting(false)
      setWaitingTS(false)
      setShowTS(false)
      setTToken('')
    }
  }, [buildSubmitPayload, onSubmitSuccess, validateAllSteps, adminMode, onAdminSubmit])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (submitting) return
    const issues = validateStep(step)
    if (issues.length) {
      setErr(issues[0])
      setShowErrors(true)
      setValidatedStep(step)
      if (step === 3) setDocsAttempted(true)
      return
    }
    if (!adminMode && !tToken) {
      if (!SITE_KEY) {
        setErr('Verification is unavailable right now. Please try again shortly.')
        return
      }
      setShowTS(true)
      setWaitingTS(true)
      setSubmitting(true)
      return
    }
    submitApplication()
  }

  const docsChecklist = [
    { label: 'CV / Resume, Portfolio, or Supporting Documents', ok: !!docKeys.cvKey, name: fileNameFromKey(docKeys.cvKey) },
    { label: 'Teaching credential / registration (optional)', ok: !!docKeys.teachingCredentialKey, name: fileNameFromKey(docKeys.teachingCredentialKey) },
    {
      label: 'Government-issued photo ID upload OR Stripe ID verification',
      ok: hasIdentityProof,
      name: docKeys.id1Key ? fileNameFromKey(docKeys.id1Key) : (stripeIdentityVerified ? 'Stripe verified' : ''),
    },
    { label: 'Profile photo', ok: !!docKeys.photoKey, name: fileNameFromKey(docKeys.photoKey) },
  ]
  const docErrorsVisible = showErrors && docsAttempted && validatedStep === 3 && step === 3
  if (loadingStatus) {
    return (
      <div
        className={isInline ? '' : 'page doc-form-shell'}
        style={{
          minHeight: isInline ? 'auto' : '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: isInline ? '1rem 0' : undefined,
        }}
      >
        <Spinner label="Checking your Education profile…" size={36} />
      </div>
    )
  }

  if (!isInline && regStatus === 'APPROVED' && !adminMode) {
    return (
      <div className="page doc-form-shell" style={{ maxWidth: 720, margin: '0 auto', padding: '1.5rem 1rem' }}>
        <div className="card" style={{ padding: '1.5rem' }}>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <ShieldCheck size={28} color="#16a34a" />
            You’re approved 🎉
          </h1>
          <p style={{ color: '#475569' }}>
            Your education profile is live. Keep your availability fresh and reply quickly to stay at the top of recommendations.
          </p>
          <div className="inline-gap-2" style={{ marginTop: 18 }}>
            <a className="btn btn-primary" href="/dashboard">Open Work hub</a>
          </div>
        </div>
      </div>
    )
  }

  if (!isInline && regStatus === 'PENDING') {
    return (
      <div className="page doc-form-shell" style={{ maxWidth: 720, margin: '0 auto', padding: '1.5rem 1rem' }}>
        <PendingApprovalCard />
      </div>
    )
  }

  const sectionTitle = isInline ? 'Update Education profile' : ''
  const sectionDescription = isInline
    ? 'Submitting updates pauses your listing until we re-approve it.'
    : null

  return (
    <div className={isInline ? '' : 'page doc-form-shell'} style={isInline ? { padding: '0 0 1.5rem' } : { maxWidth: 980, margin: '0 auto', padding: '1.5rem 1rem 2.5rem' }}>
      {!isInline && (
        <div className="card" style={{ marginBottom: 16, padding: '16px 18px' }}>
          <p className="hero-kicker">Professionals · Education & Tutoring</p>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <GraduationCap size={28} />
            Education & Tutoring
          </h1>
          <p className="hint">List your education services, verify your credentials, and start accepting paid match requests nationwide.</p>
        </div>
      )}
      <div className="stepper" style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {steps.map((label, idx) => {
          const active = idx === step
          const done = idx < step
          const isFuture = idx > step
          return (
            <button
              key={label}
              type="button"
              className="chip"
              style={{
                background: active ? '#0f766e' : done ? '#e0f2fe' : '#f8fafc',
                color: active ? '#fff' : '#0f172a',
                borderColor: active ? '#0f766e' : '#cbd5e1',
                cursor: active ? 'default' : 'pointer',
              }}
              onClick={() => {
                if (idx === step) return
                if (isFuture) {
                  const issues = validateStep(step)
                  if (issues.length) {
                    setShowErrors(true)
                    setValidatedStep(step)
                    setErr(issues[0])
                    return
                  }
                }
                setShowErrors(false)
                setValidatedStep(null)
                setErr('')
                setStep(idx)
              }}
            >
              {label}
            </button>
          )
        })}
      </div>

      <Section title={sectionTitle}>
        {sectionDescription && (
          <p className="sub" style={{ marginBottom: 16 }}>
            {sectionDescription}
          </p>
        )}
        {isInline && inlineNotice && (
          <div className="banner warn" style={{ marginBottom: 16 }}>
            {inlineNotice}
          </div>
        )}
        {info && (
          <div className="banner success" style={{ marginBottom: 12 }}>
            {info}
          </div>
        )}
        <form className="card doc-form-card" style={{ padding: isInline ? '0 0 1.5rem' : '1.5rem', paddingBottom: '6rem' }} onSubmit={(e) => e.preventDefault()}>
          <section className="section" style={{ display: step === 0 ? 'block' : 'none' }}>
            <h2 className="section-title">Identity & Compliance</h2>
            <div className="two-up">
              <label className="field">
                <div className="field-label">First name *</div>
                <input className="input" placeholder="First name" value={s.firstName} onChange={(e) => setS({ ...s, firstName: e.target.value })} />
              </label>
              <label className="field">
                <div className="field-label">Last name *</div>
                <input className="input" placeholder="Last name" value={s.lastName} onChange={(e) => setS({ ...s, lastName: e.target.value })} />
              </label>
            </div>
            <label className="field">
              <div className="field-label">Email *</div>
              <input className="input" value={s.email} readOnly />
              <p className="hint">Uses your sign-in email. Update it via account settings.</p>
            </label>
            <label className="field">
              <div className="field-label">Country / Territory *</div>
              <select
                className="input"
                value={countryCode}
                onChange={(e) => {
                  const nextCode = normalizeCountryCode(e.target.value)
                  const nextRule = getCountryRule(nextCode)
                  const nextRegions = nextRule.regionOptions || []
                  setS((prev: any) => {
                    const nextState = nextRegions.length
                      ? (nextRegions.includes(prev.state) ? prev.state : (nextRule.defaultRegion || nextRegions[0] || ''))
                      : ''
                    return {
                      ...prev,
                      countryCode: nextCode,
                      state: nextState,
                      postcode: nextRule.requirePostal ? prev.postcode : '',
                    }
                  })
                }}
              >
                {COUNTRY_OPTIONS.map((opt) => (
                  <option key={opt.code} value={opt.code}>{opt.label}</option>
                ))}
              </select>
            </label>
            <div className="two-up">
              <label className="field">
                <div className="field-label">Mobile *</div>
                <input className="input" placeholder={phonePlaceholder} value={s.phone} onChange={(e) => setS({ ...s, phone: e.target.value })} />
              </label>
              <label className="field">
                <div className="field-label">{educationRegistrationRule.fieldLabelOptional}</div>
                <input
                  className="input"
                  value={s.teacherRegistrationNumber}
                  onChange={(e) => setS({ ...s, teacherRegistrationNumber: e.target.value })}
                  placeholder={educationRegistrationRule.placeholder}
                />
              </label>
            </div>
            <label className="field">
              <div className="field-label">Street address *</div>
              <input className="input" placeholder="Unit / street" value={s.street} onChange={(e) => setS({ ...s, street: e.target.value })} />
            </label>
            <div className="two-up">
              <label className="field">
                <div className="field-label">Suburb / City *</div>
                <input className="input" placeholder="Suburb or city" value={s.city} onChange={(e) => setS({ ...s, city: e.target.value })} />
              </label>
              <label className="field">
                <div className="field-label">
                  {countryRule.regionLabel}
                  {countryRule.requireRegion ? ' *' : ' (optional)'}
                </div>
                {regionOptions.length > 0 ? (
                  <select className="input" value={s.state} onChange={(e) => setS({ ...s, state: e.target.value as any })}>
                    {!countryRule.requireRegion && <option value="">Select {countryRule.regionLabel}</option>}
                    {regionOptions.map((st) => (
                      <option key={st} value={st}>{st}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    className="input"
                    placeholder={countryRule.regionLabel}
                    value={s.state}
                    onChange={(e) => setS({ ...s, state: e.target.value })}
                  />
                )}
              </label>
            </div>
            {countryRule.requirePostal && (
              <label className="field">
                <div className="field-label">{countryRule.postalLabel} *</div>
                <input
                  className="input"
                  placeholder={countryRule.postalPlaceholder}
                  value={s.postcode}
                  onChange={(e) => setS({ ...s, postcode: e.target.value })}
                />
              </label>
            )}
            <div className="section" style={{ marginTop: 10 }}>
              <button
                type="button"
                className="btn-outline"
                onClick={() => {
                  setShowOptionalIdentity((v) => {
                    const next = !v
                    if (next) setSyncPublicContact(true)
                    return next
                  })
                }}
              >
                {showOptionalIdentity ? 'Hide optional details' : `Show optional details (${taxIdShortLabel}, client contact)`}
              </button>
              {showOptionalIdentity && (
                <div className="card" style={{ marginTop: 12, padding: 12, display: 'grid', gap: 12 }}>
                  <div className="two-up">
                    <label className="field">
                      <div className="field-label">{taxIdLabel}</div>
                      <input className="input" value={s.abn} onChange={(e) => setS({ ...s, abn: e.target.value })} placeholder={taxIdPlaceholder} />
                    </label>
                  <label className="field">
                    <div className="field-label">Business / trading name (optional)</div>
                    <input className="input" value={s.practiceName} onChange={(e) => setS({ ...s, practiceName: e.target.value })} placeholder="Practice or tutoring brand" />
                  </label>
                </div>
                <div className="card" style={{ background: '#f8fafc' }}>
                  <h3 className="field-label" style={{ marginTop: 0 }}>Client-facing contact</h3>
                  <label className="checkbox">
                    <input
                      type="checkbox"
                        checked={syncPublicContact}
                        onChange={(e) => setSyncPublicContact(e.target.checked)}
                      />
                      <span>Use the same email and phone for clients</span>
                    </label>
                    {!syncPublicContact && (
                      <div className="two-up" style={{ marginTop: 8 }}>
                        <label className="field">
                          <div className="field-label">Public email</div>
                          <input className="input" value={s.publicEmail} onChange={(e) => setS({ ...s, publicEmail: e.target.value })} />
                        </label>
                        <label className="field">
                          <div className="field-label">Public phone</div>
                          <input className="input" value={s.publicPhone} onChange={(e) => setS({ ...s, publicPhone: e.target.value })} placeholder={phonePlaceholder} />
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </section>

          <section className="section" style={{ display: step === 1 ? 'block' : 'none' }}>
            <h2 className="section-title">Specialties</h2>
            <p className="minor" style={{ marginBottom: 12 }}>
              Select every service you can confidently deliver. Clients can only book you for the areas of expertise you list here.
            </p>
            <div className="chips" style={{ flexWrap: 'wrap' }}>
              {s.specialties.map((sp: string) => (
                <span key={sp} className="chip">
                  {sp}
                  <button
                    type="button"
                    className="chip-x"
                    aria-label={`Remove ${sp}`}
                    onClick={() => setS((prev: any) => ({ ...prev, specialties: prev.specialties.filter((x: string) => x !== sp) }))}
                  >
                    ×
                  </button>
                </span>
              ))}
              {s.customSpecialties.map((sp: string) => (
                <span key={`custom-${sp}`} className="chip">
                  {`Other: ${sp}`}
                  <button
                    type="button"
                    className="chip-x"
                    aria-label={`Remove ${sp}`}
                    onClick={() => setS((prev: any) => ({ ...prev, customSpecialties: prev.customSpecialties.filter((x: string) => x !== sp) }))}
                  >
                    ×
                  </button>
                </span>
              ))}
              {!s.specialties.length && !s.customSpecialties.length && <span className="hint">No areas selected</span>}
            </div>
            <div className="inline-gap-2" style={{ marginTop: 10 }}>
              <button type="button" className="btn-outline" onClick={() => setSpecOpen(true)}>
                {s.specialties.length + s.customSpecialties.length ? 'Edit areas of expertise' : 'Add areas of expertise'}
              </button>
              <span className="hint">Selected: {s.specialties.length + s.customSpecialties.length}</span>
            </div>
          </section>

          <section className="section" style={{ display: step === 2 ? 'block' : 'none' }}>
            <h2 className="section-title">Profile & pricing</h2>
            <label className="field">
              <div className="field-label">Short bio *</div>
              <textarea
                rows={4}
                value={s.profile}
                onChange={(e) => setS({ ...s, profile: e.target.value.slice(0, PROFILE_CHAR_LIMIT) })}
              />
              <div className="hint">{PROFILE_CHAR_LIMIT - s.profile.length} characters remaining</div>
            </label>
            <div className="two-up">
              <label className="field">
                <div className="field-label">{`Minimum fee (${feeCurrency}) per 30 min session *`}</div>
                <input
                  className="input"
                  type="number"
                  min="0"
                  step="1"
                  value={s.pricingMin}
                  onChange={(e) => setS({ ...s, pricingMin: e.target.value })}
                />
                {pricingErrors.min && <div className="hint" style={{ color: '#b91c1c' }}>{pricingErrors.min}</div>}
              </label>
              <label className="field">
                <div className="field-label">{`Maximum fee (${feeCurrency}) per 30 min session *`}</div>
                <input
                  className="input"
                  type="number"
                  min="0"
                  step="1"
                  value={s.pricingMax}
                  onChange={(e) => setS({ ...s, pricingMax: e.target.value })}
                />
                {pricingErrors.max && <div className="hint" style={{ color: '#b91c1c' }}>{pricingErrors.max}</div>}
              </label>
            </div>
            <label className="field">
              <div className="field-label">Pricing notes (optional)</div>
              <textarea rows={2} value={s.pricingNotes} onChange={(e) => setS({ ...s, pricingNotes: e.target.value.slice(0, 200) })} />
              <div className="hint">{200 - s.pricingNotes.length} characters remaining</div>
              {pricingErrors.notes && <div className="hint" style={{ color: '#b91c1c' }}>{pricingErrors.notes}</div>}
            </label>
          </section>

          <section className="section" style={{ display: step === 3 ? 'block' : 'none' }}>
            <h2 className="section-title">Documents & Verification</h2>
            <div className="card" style={{ marginBottom: 12, padding: 12 }}>
              <div className="field-label" style={{ marginBottom: 4 }}>Identity verification (choose ONE option)</div>
              <p className="identity-choice-help">Choose ONE option only for ID verification: Option A complete Stripe verification OR Option B upload your photo ID. Do not complete both.</p>
              <div className="inline-gap-2" style={{ flexWrap: 'wrap' }}>
                {docsChecklist.map((row) => (
                  <div
                    key={row.label}
                    className="chip"
                    style={{
                      background: row.ok ? '#ecfdf3' : '#f8fafc',
                      borderColor: row.ok ? '#16a34a' : '#cbd5e1',
                      color: row.ok ? '#166534' : '#0f172a',
                    }}
                  >
                    {row.ok ? <CheckCircle2 size={16} style={{ marginRight: 6 }} /> : <span style={{ marginRight: 6 }}>•</span>}
                    <span>
                      {row.label}
                      {row.ok && row.name ? ` — ${row.name}` : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            {docErrorsVisible && (
              <div className="banner error" role="alert" style={{ marginBottom: 12 }}>
                Upload your CV / Resume, Portfolio, or Supporting Documents
              </div>
            )}
            <p className="hint" style={{ marginBottom: 12 }}>Files up to 15 MB each.</p>
            <div className="doc-grid">
              <FilePickerField
                label="CV / Resume, Portfolio, or Supporting Documents"
                required
                value={s.cvFile}
                onChange={(file) => handleDocSelect('cvKey', file, 'cv')}
                uploadedLabel={fileNameFromKey(docKeys.cvKey)}
              />
              <FilePickerField
                label="Teaching credential / registration"
                value={s.credentialFile}
                onChange={(file) => handleDocSelect('teachingCredentialKey', file, 'credential')}
                uploadedLabel={fileNameFromKey(docKeys.teachingCredentialKey)}
              />
              <FilePickerField
                label="Profile photo"
                required
                value={s.photoFile}
                onChange={(file) => handleDocSelect('photoKey', file, 'photo')}
                uploadedLabel={fileNameFromKey(docKeys.photoKey)}
              />
              <StripeIdentityVerifyPanel
                className="identity-choice-card identity-choice-card--stripe"
                enabled={!adminMode && !!user}
                countryCode={countryCode}
                onChange={(snap) => setStripeIdentityVerified(Boolean(snap.verified))}
              />
              <div className="identity-choice-or" aria-hidden="true">OR</div>
              <FilePickerField
                className="identity-choice-card identity-choice-card--upload"
                label="Option B: Upload government-issued photo ID"
                value={s.idFile1}
                onChange={(file) => handleDocSelect('id1Key', file, 'id1')}
                uploadedLabel={fileNameFromKey(docKeys.id1Key)}
                hint="Option B (upload): Passport, driver licence, or national ID card. No Stripe fee."
              />
            </div>
          </section>

          {SITE_KEY && showTS && (
            <div className="field" style={{ marginTop: 16 }}>
              <div className="field-label">Quick verification</div>
              <Turnstile
                siteKey={SITE_KEY}
                action="register_specialist"
                onToken={(token) => {
                  setTToken(token)
                  if (token) {
                    setShowTS(false)
                  }
                  if (!token) {
                    setWaitingTS(false)
                    setSubmitting(false)
                  }
                }}
              />
            </div>
          )}

          <div className="actions" style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <div className="inline-gap-2">
              {step > 0 && (
                <button type="button" className="btn" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={submitting}>
                  Back
                </button>
              )}
              {step < steps.length - 1 && (
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={submitting}
                  onClick={() => {
                    const issues = validateStep(step)
                    if (issues.length) {
                      setErr(issues[0])
                      setShowErrors(true)
                      setValidatedStep(step)
                      return
                    }
                    setShowErrors(false)
                    setValidatedStep(null)
                    setErr('')
                    setStep((s) => Math.min(steps.length - 1, s + 1))
                  }}
                >
                  Next
                </button>
              )}
            </div>
            {step < steps.length - 1 && (
              <button
                className="btn-outline"
                type="button"
                disabled={submitting}
                onClick={async () => {
                  try {
                    setSaveVisible(true)
                    setSaveStatus('saving')
                    await saveDraft()
                    setErr('')
                  } catch (e: any) {
                    setErr('Save failed. Try again.')
                    setSaveStatus('error')
                  }
                }}
                style={{ marginLeft: 'auto' }}
              >
                Save & finish later
              </button>
            )}
            {step === steps.length - 1 && (
              <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit application'}
              </button>
            )}
          </div>
          {err && (
            <div className="banner error" role="alert" style={{ marginTop: 10 }}>
              {err}
            </div>
          )}
        </form>
      </Section>

      {specOpen && (
        <div className="overlay show" role="dialog" aria-modal="true">
          <div className="sheet" id="spec-sheet">
            <header className="sheet-header">
              <div className="sheet-title">Select areas of expertise</div>
              <button
                type="button"
                className="btn-ghost"
                onClick={() => {
                  setSpecOpen(false)
                  setSpecQuery('')
                }}
              >
                Close
              </button>
            </header>
            <div className="sheet-body">
              <input
                className="input"
                placeholder="Search areas…"
                value={specQuery}
                onChange={(e) => setSpecQuery(e.target.value)}
                aria-label="Search areas"
              />
              <div className="spec-list">
                {filteredSpecialties.map(({ group, items }) => {
                  if (!items.length) return null
                  return (
                    <div key={group} className="card" style={{ marginBottom: 12 }}>
                      <div className="field-label" style={{ marginBottom: 8 }}>{group}</div>
                      <div className="chip-row">
                        {items.map((sp) => (
                          <button
                            type="button"
                            key={sp}
                            className={`chip ${s.specialties.includes(sp) ? 'active' : ''}`}
                            onClick={() => {
                              setS((prev: any) => {
                                const exists = prev.specialties.includes(sp)
                                if (exists) {
                                  return { ...prev, specialties: prev.specialties.filter((x: string) => x !== sp) }
                                }
                                return { ...prev, specialties: [...prev.specialties, sp] }
                              })
                            }}
                          >
                            {sp}
                          </button>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
              <div className="card" style={{ marginTop: 12 }}>
                <div className="field-label" style={{ marginBottom: 6 }}>Other areas (short text)</div>
                <div className="inline-gap-2" style={{ alignItems: 'flex-start' }}>
                  <input
                    className="input"
                    placeholder="Add another area"
                    value={customSpec}
                    onChange={(e) => {
                      setCustomSpec(e.target.value)
                      setCustomSpecErr('')
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        addCustomSpecialty()
                      }
                    }}
                  />
                  <button type="button" className="btn-outline" onClick={addCustomSpecialty}>
                    Add
                  </button>
                </div>
                {s.customSpecialties.length > 0 && (
                  <div className="chip-row" style={{ marginTop: 8 }}>
                    {s.customSpecialties.map((sp: string) => (
                      <button
                        type="button"
                        key={`custom-${sp}`}
                        className="chip active"
                        onClick={() =>
                          setS((prev: any) => ({
                            ...prev,
                            customSpecialties: prev.customSpecialties.filter((x: string) => x !== sp),
                          }))
                        }
                        aria-label={`Remove ${sp}`}
                      >
                        {`Other: ${sp}`} ×
                      </button>
                    ))}
                  </div>
                )}
                {customSpecErr ? (
                  <div className="hint" style={{ color: '#dc2626', marginTop: 6 }}>{customSpecErr}</div>
                ) : (
                  <div className="hint" style={{ marginTop: 6 }}>
                    {customSuggestions.length ? (
                      <>Similar existing: {customSuggestions.join(' · ')}</>
                    ) : (
                      'Add any other areas that aren’t listed above.'
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="sheet-footer" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="hint">Selected: {s.specialties.length + s.customSpecialties.length}</span>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setSpecOpen(false)
                  setSpecQuery('')
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
      {saveVisible && saveStatus !== 'idle' && (
        <div
          style={{
            position: 'fixed',
            bottom: 80,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1500,
            background: '#0f766e',
            color: '#fff',
            borderRadius: 999,
            padding: '12px 18px',
            boxShadow: '0 12px 30px rgba(0,0,0,0.18)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontSize: 14,
            minWidth: 180,
            justifyContent: 'center',
          }}
          aria-live="polite"
        >
          <GraduationCap size={16} />
          <span>
            {saveStatus === 'saving' && 'Saving draft…'}
            {saveStatus === 'saved' && 'Saved'}
            {saveStatus === 'error' && 'Save failed (retrying on next change)'}
          </span>
        </div>
      )}
    </div>
  )
}
